import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ObjetoService } from 'app/core/services/objeto.service';
import { Objeto } from 'app/core/models/objeto.model';
import { MenuItem } from './menu-item';

import { ICONE, ORDENACAO } from './menu-param.constants';
import { UsuarioService } from 'app/core/services/usuario.service';
import { NgClass } from '@angular/common';
import { OabProfileDropdownV2Component } from '../oab-profile-dropdown/oab-profile-dropdown.component';

@Component({
    selector: 'app-menu-left-v2',
    templateUrl: './menu-left-vertical.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        RouterLinkActive,
        NgClass,
        OabProfileDropdownV2Component,
    ],
})
export class MenuLeftV2Component implements OnInit {
    private router = inject(Router);
    private objetoService = inject(ObjetoService);
    private usuarioService = inject(UsuarioService);
    private destroyRef = inject(DestroyRef);

    idServico = 'b2aec844-32a6-461f-8276-333e19dab857';
    readonly nodes = signal<MenuItem[]>([]);
    // "Base de Conhecimento" (documentação): vem do menu do servidor, mas é fixado no rodapé (acima do perfil).
    readonly bottomNode = signal<MenuItem | null>(null);
    perfis: string[];
    collapsed = false;

    private readonly COLLAPSE_KEY = 'oab-sidebar-collapsed';

    ngOnInit() {
        // Restaura o estado recolhido/expandido da sidebar (width-based, estilo Vercel/GitHub/Linear).
        this.collapsed = localStorage.getItem(this.COLLAPSE_KEY) === '1';
        this.applyCollapseClass();

        this.perfis = this.usuarioService.getRole();
        this.objetoService.menuUser(this.idServico).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
            const all = (res.data ?? [])
                // .filter(this.filterMenuByProfile)
                .map(this.transformNodes)
                .sort(this.sortNodes);

            // "Base de Conhecimento" é fixado no rodapé (acima do perfil): removemos da lista e o
            // renderizamos separado. Continua server-driven/por perfil — se o perfil não tiver o
            // objeto MENU, não aparece nem em cima nem embaixo.
            const isBottom = (n: MenuItem) => (n.routerLink ?? '').toLowerCase().includes('base-conhecimento');
            this.bottomNode.set(all.find(isBottom) ?? null);
            this.nodes.set(all.filter(n => !isBottom(n)));

            // Abre o submenu que contém a rota atual (antes dependia do CleanUI/jQuery).
            this.expandActiveSubmenu();
        });
    }

    // Accordion nativo (substitui o toggleClass + slideToggle do menu-left.cleanui.js).
    // Só um submenu de raiz fica aberto por vez, como no comportamento original.
    toggleSubmenu(node: MenuItem): void {
        const willOpen = !node.expanded;
        const nodes = this.nodes();
        nodes.forEach((n) => (n.expanded = false));
        node.expanded = willOpen;
        // Mutação interna dos itens não dispara o signal; reemite a referência p/ OnPush refletir.
        this.nodes.set([...nodes]);
    }

    private expandActiveSubmenu(): void {
        const url = this.router.url;
        const nodes = this.nodes();
        for (const node of nodes) {
            node.expanded = (node.children ?? []).some(
                (child) => child.routerLink && url.startsWith(child.routerLink)
            );
        }
        this.nodes.set([...nodes]);
    }

    toggleCollapse() {
        this.collapsed = !this.collapsed;
        localStorage.setItem(this.COLLAPSE_KEY, this.collapsed ? '1' : '0');
        // Recolhida usa flyout por hover; zera os accordions abertos p/ não conflitar.
        if (this.collapsed) {
            const nodes = this.nodes();
            nodes.forEach((n) => (n.expanded = false));
            this.nodes.set([...nodes]);
        } else {
            this.expandActiveSubmenu();
        }
        this.applyCollapseClass();
    }

    private applyCollapseClass() {
        // A classe vai no <body> porque o conteúdo (.cat__content) e a sidebar (.cat__menu-left)
        // são irmãos no DOM; o estado precisa alcançar ambos via seletor de ancestral comum.
        document.body.classList.toggle('oab-sidebar-collapsed', this.collapsed);
    }

    private filterMenuByProfile = (node: Objeto): boolean => {
        const objetos = Object.keys(node.otherParents);        

        for (const perfil of this.perfis) {
            // perfil é o nome do papel (role). O vínculo é por otherParents do nó.
            // (Removida cláusula morta `node.idenServ == perfil.idenServ`: perfil é string, nunca tinha idenServ.)
            if (objetos.includes(perfil)) {
                return true;
            }
        }

        return false;
    }

    private transformNodes = (node: Objeto): MenuItem => {
        const paramIcon = node.parametros.find((el) => el.chavParmObje === ICONE);
        const ordenacao = node.parametros.find((el) => el.chavParmObje === ORDENACAO);
        const icon = paramIcon ? paramIcon.valrParmObje : '';

        return {
            name: node.nomeObje,
            routerLink: node.endeUrl,
            ordenacao: ordenacao ? parseInt(ordenacao.valrParmObje, 10) : 0,
            // Reativado (com fallback) para a sidebar recolhida mostrar apenas ícones.
            icon: icon || 'fa fa-circle-o',
            children: (node.children ?? [])
                // .filter(this.filterMenuByProfile)
                .map(this.transformNodes)
                .sort(this.sortNodes)
        };
    }

    private sortNodes = (a, b) => {
        return a.ordenacao < b.ordenacao ? -1 : a.ordenacao > b.ordenacao ? 1 : 0;
    }
}
